import { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { invitationDiagnosticsCache, InvitationTimeline } from '../../api/modules/realtimeMessaging/invitationDiagnosticsCache';
import { Invitation } from '@playcastdotio/Messaging';
import { InvitationTimelineScrubber } from '../components/dev/InvitationTimelineScrubber';

type CachedInvitation = {
  invitation: Invitation;
  lastSeen: number;
  reason: 'added' | 'updated' | 'removed';
  timestamp: number;
};

type GroupedRow = {
  reasonKey: string;
  entries: CachedInvitation[];
};

const formatTimestamp = (timestamp: number): string => {
  return new Date(timestamp).toLocaleString();
};

const formatDuration = (timestamp: number): string => {
  const now = Date.now();
  const diff = now - timestamp;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days}d ${hours % 24}h ago`;
  if (hours > 0) return `${hours}h ${minutes % 60}m ago`;
  if (minutes > 0) return `${minutes}m ago`;
  return 'Just now';
};

export const DevInvitations = () => {
  const [cachedInvitations, setCachedInvitations] = useState<CachedInvitation[]>([]);
  const [filterReason, setFilterReason] = useState<'all' | 'added' | 'updated' | 'removed'>('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isEnabled, setIsEnabled] = useState(invitationDiagnosticsCache.isEnabled());
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedTimeline, setSelectedTimeline] = useState<InvitationTimeline | null>(null);
  const scrollPositionRef = useRef<number>(0);
  const containerRef = useRef<HTMLDivElement>(null);

  // Preserve scroll position during updates
  const preserveScrollPosition = useCallback(() => {
    if (containerRef.current) {
      scrollPositionRef.current = containerRef.current.scrollTop;
    }
  }, []);

  const restoreScrollPosition = useCallback(() => {
    if (containerRef.current && scrollPositionRef.current > 0) {
      containerRef.current.scrollTop = scrollPositionRef.current;
    }
  }, []);

  const refreshData = useCallback(() => {
    preserveScrollPosition();
    setIsUpdating(true);

    const newData = invitationDiagnosticsCache.getAll();
    const newEnabled = invitationDiagnosticsCache.isEnabled();

    // Only update if data actually changed to prevent unnecessary re-renders
    setCachedInvitations((prevData) => {
      const prevIds = prevData
        .map((d) => d.invitation?.id)
        .filter(Boolean)
        .sort();
      const newIds = newData
        .map((d) => d.invitation?.id)
        .filter(Boolean)
        .sort();
      const dataChanged = JSON.stringify(prevIds) !== JSON.stringify(newIds);
      return dataChanged ? newData : prevData;
    });

    setIsEnabled((prevEnabled) => (prevEnabled !== newEnabled ? newEnabled : prevEnabled));

    // Restore scroll position after a brief delay to allow rendering
    setTimeout(() => {
      restoreScrollPosition();
      setIsUpdating(false);
    }, 50);
  }, [preserveScrollPosition, restoreScrollPosition]);

  useEffect(() => {
    refreshData();
    const interval = setInterval(refreshData, 3000); // Reduced frequency to 3 seconds
    return () => clearInterval(interval);
  }, [refreshData]);

  const filteredInvitations = useMemo(() => {
    return cachedInvitations.filter((cached) => {
      // Skip invalid entries
      if (!cached?.invitation?.id) {
        return false;
      }

      // Filter by reason
      if (filterReason !== 'all' && cached.reason !== filterReason) {
        return false;
      }

      // Filter by search term
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        const matchesId = cached.invitation.id?.toLowerCase().includes(searchLower);
        const matchesTitle = cached.invitation.description?.toLowerCase().includes(searchLower);
        const matchesOwner = cached.invitation.owner?.username?.toLowerCase().includes(searchLower);
        const matchesType = cached.invitation.type?.toLowerCase().includes(searchLower);

        if (!matchesId && !matchesTitle && !matchesOwner && !matchesType) {
          return false;
        }
      }

      return true;
    });
  }, [cachedInvitations, filterReason, searchTerm]);

  const grouped = useMemo<GroupedRow[]>(() => {
    const byReason: Record<string, CachedInvitation[]> = {};
    for (const cached of filteredInvitations) {
      const key = cached.reason;
      byReason[key] = byReason[key] || [];
      byReason[key].push(cached);
    }

    const rows: GroupedRow[] = Object.entries(byReason)
      .map(([reasonKey, entries]) => ({
        reasonKey,
        entries: entries.sort((a, b) => b.timestamp - a.timestamp), // Most recent first
      }))
      .sort((a, b) => a.reasonKey.localeCompare(b.reasonKey));

    return rows;
  }, [filteredInvitations]);

  const stats = invitationDiagnosticsCache.getStats();

  const clearCache = () => {
    // Close any open timeline scrubber first
    setSelectedTimeline(null);

    // Clear the cache
    invitationDiagnosticsCache.clear();

    // Immediately clear React state to prevent stale references
    setCachedInvitations([]);

    // Then refresh data (should be empty)
    setTimeout(() => {
      refreshData();
    }, 100);
  };

  const toggleCache = () => {
    invitationDiagnosticsCache.setEnabled(!isEnabled);
    refreshData();
  };

  return (
    <div ref={containerRef} className='min-h-screen overflow-auto bg-neutral-950 p-6 text-neutral-100'>
      <div className='mb-6'>
        <div className='mb-4 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between'>
          <h1 className='text-3xl font-bold'>Invitation Diagnostics</h1>
          <div className='flex gap-2'>
            <button onClick={toggleCache} className={`rounded px-3 py-1 text-white ${isEnabled ? 'bg-green-600' : 'bg-gray-600'}`}>
              {isEnabled ? 'Enabled' : 'Disabled'}
            </button>
            <button onClick={clearCache} className='rounded bg-red-600 px-3 py-1 text-white'>
              Clear Cache
            </button>
          </div>
        </div>

        {/* Stats */}
        <div
          className={`mb-6 grid grid-cols-2 gap-4 transition-opacity duration-200 sm:grid-cols-5 ${isUpdating ? 'opacity-75' : 'opacity-100'}`}
        >
          <div className='rounded border border-neutral-700 bg-neutral-900 p-4 transition-all duration-200'>
            <div className='min-w-[3ch] font-mono text-2xl font-bold text-blue-400'>{stats.totalItems}</div>
            <div className='text-sm text-neutral-400'>Total</div>
          </div>
          <div className='rounded border border-neutral-700 bg-neutral-900 p-4 transition-all duration-200'>
            <div className='min-w-[3ch] font-mono text-2xl font-bold text-green-400'>{stats.reasonCounts.added || 0}</div>
            <div className='text-sm text-neutral-400'>Added</div>
          </div>
          <div className='rounded border border-neutral-700 bg-neutral-900 p-4 transition-all duration-200'>
            <div className='min-w-[3ch] font-mono text-2xl font-bold text-yellow-400'>{stats.reasonCounts.updated || 0}</div>
            <div className='text-sm text-neutral-400'>Updated</div>
          </div>
          <div className='rounded border border-neutral-700 bg-neutral-900 p-4 transition-all duration-200'>
            <div className='min-w-[3ch] font-mono text-2xl font-bold text-red-400'>{stats.reasonCounts.removed || 0}</div>
            <div className='text-sm text-neutral-400'>Removed</div>
          </div>
          <div className='rounded border border-neutral-700 bg-neutral-900 p-4 transition-all duration-200'>
            <div className='min-w-[3ch] font-mono text-2xl font-bold text-purple-400'>{stats.maxSize}</div>
            <div className='text-sm text-neutral-400'>Max Size</div>
          </div>
        </div>

        {/* Filters */}
        <div className='mb-4 flex flex-col gap-4 sm:flex-row'>
          <div className='flex gap-2'>
            <button
              onClick={() => setFilterReason('all')}
              className={`rounded px-3 py-1 ${filterReason === 'all' ? 'bg-blue-600 text-white' : 'bg-neutral-800 text-neutral-300'}`}
            >
              All ({cachedInvitations.length})
            </button>
            <button
              onClick={() => setFilterReason('removed')}
              className={`rounded px-3 py-1 ${filterReason === 'removed' ? 'bg-red-600 text-white' : 'bg-neutral-800 text-neutral-300'}`}
            >
              Removed ({stats.reasonCounts.removed || 0})
            </button>
            <button
              onClick={() => setFilterReason('updated')}
              className={`rounded px-3 py-1 ${filterReason === 'updated' ? 'bg-yellow-600 text-white' : 'bg-neutral-800 text-neutral-300'}`}
            >
              Updated ({stats.reasonCounts.updated || 0})
            </button>
          </div>

          <input
            type='text'
            placeholder='Search invitations...'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className='flex-1 rounded border border-neutral-700 bg-neutral-800 px-3 py-1 text-neutral-100 placeholder-neutral-400 transition-colors duration-200 focus:border-blue-500 focus:outline-none'
          />
        </div>
      </div>

      {!isEnabled ? (
        <div className='rounded border border-amber-600 bg-amber-900/20 p-4 text-amber-200'>
          <h3 className='font-semibold'>Cache Disabled</h3>
          <p>The invitation diagnostics cache is currently disabled. Enable it to start collecting diagnostic data.</p>
        </div>
      ) : filteredInvitations.length === 0 ? (
        <div className='text-neutral-400'>
          {cachedInvitations.length === 0
            ? 'No invitations cached yet. Cache will populate as invitations are processed.'
            : 'No invitations match the current filters.'}
        </div>
      ) : (
        <div className='space-y-4'>
          {grouped.map((group) => (
            <div key={group.reasonKey} className='overflow-hidden rounded border border-neutral-700 bg-neutral-900'>
              <div
                className={`px-4 py-3 text-sm font-medium ${
                  group.reasonKey === 'removed'
                    ? 'bg-red-900 text-red-200'
                    : group.reasonKey === 'added'
                      ? 'bg-green-900 text-green-200'
                      : 'bg-yellow-900 text-yellow-200'
                }`}
              >
                {group.reasonKey.charAt(0).toUpperCase() + group.reasonKey.slice(1)} ({group.entries.length})
              </div>
              <div className='divide-y divide-neutral-800'>
                {group.entries
                  .filter((cached) => cached?.invitation?.id)
                  .map((cached, idx) => (
                    <div key={`${cached.invitation.id}-${cached.timestamp}`} className='p-4'>
                      <div className='mb-2 flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between'>
                        <div className='flex-1'>
                          <div className='mb-1 flex items-center gap-2'>
                            <span className='font-mono text-sm text-blue-400'>{cached.invitation.id}</span>
                            <span
                              className={`rounded px-2 py-0.5 text-xs ${
                                cached.invitation.type === 'cafe' ? 'bg-green-800 text-green-200' : 'bg-purple-800 text-purple-200'
                              }`}
                            >
                              {cached.invitation.type}
                            </span>
                            {cached.invitation.invalid && (
                              <span className='rounded bg-red-800 px-2 py-0.5 text-xs text-red-200'>Invalid</span>
                            )}
                          </div>
                          <div className='text-sm text-neutral-300'>
                            <strong>Name:</strong> {cached.invitation.name || cached.invitation.description || 'No name'}
                          </div>
                          <div className='text-sm text-neutral-300'>
                            <strong>Owner:</strong> {cached.invitation.owner?.username || cached.invitation.owner?.profileId || 'Unknown'}
                          </div>
                          {cached.invitation.manifest && cached.invitation.type === 'cafe' && (
                            <div className='text-sm text-neutral-300'>
                              <strong>Players:</strong> {cached.invitation.manifest.players?.length || 0} / {'4'}
                            </div>
                          )}
                          <div className='mt-2'>
                            <button
                              onClick={() => {
                                if (cached?.invitation?.id) {
                                  const timeline = invitationDiagnosticsCache.getTimeline(cached.invitation.id);
                                  if (timeline && timeline.snapshots.length > 0) {
                                    setSelectedTimeline(timeline);
                                  }
                                }
                              }}
                              className='rounded bg-blue-600 px-2 py-1 text-xs text-white hover:bg-blue-700'
                              disabled={!cached?.invitation?.id}
                            >
                              <span role='img' aria-label='Timeline'>
                                📊
                              </span>{' '}
                              View Timeline
                            </button>
                          </div>
                        </div>
                        <div className='text-right text-sm text-neutral-400'>
                          <div>{formatTimestamp(cached.timestamp)}</div>
                          <div className='font-mono'>{formatDuration(cached.timestamp)}</div>
                        </div>
                      </div>

                      {/* Expandable JSON details */}
                      <details className='mt-2'>
                        <summary className='cursor-pointer text-xs text-neutral-500 hover:text-neutral-300'>Show JSON Details</summary>
                        <pre className='mt-2 overflow-auto rounded bg-neutral-950 p-2 text-xs text-neutral-300'>
                          {JSON.stringify(cached.invitation, null, 2)}
                        </pre>
                      </details>
                    </div>
                  ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Timeline Scrubber Modal */}
      {selectedTimeline && selectedTimeline.snapshots.length > 0 && (
        <InvitationTimelineScrubber timeline={selectedTimeline} onClose={() => setSelectedTimeline(null)} />
      )}
    </div>
  );
};

export default DevInvitations;
